import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, AlertTriangle, Plus } from "lucide-react";
import api from "@/lib/api";

/**
 * CreditCostStrip
 * ----------------------------------------------------------------
 * Drop-in pill that every generator page should render above the
 * Generate button. Shows:
 *   • the user's current credit balance (live)
 *   • the estimated cost of the currently-selected generation
 *   • a low-balance warning when balance < cost
 *   • a top-up link / button
 *
 * Props:
 *   feature   — "image" | "video" | "voice" | "logo"  (string, required)
 *   quality   — "draft" | "pro" | "cinema"            (default: "pro")
 *   duration  — seconds, optional (for video/voice)
 *   label     — optional descriptive label like "10s · Pro"
 *
 * Owners are shown the cost line but with a "Owner · exempt from debit"
 * note instead of the balance-gate, since owners never burn credits.
 */
export default function CreditCostStrip({ feature, quality = "pro", duration, label }) {
  const [balance, setBalance]   = useState(null);
  const [cost, setCost]         = useState(null);
  const [isOwner, setIsOwner]   = useState(false);

  // Load balance once on mount + when the page tab regains focus (Stripe redirect)
  useEffect(() => {
    const load = () =>
      api.get("/credits/me")
        .then(({ data }) => {
          setBalance(data?.balance ?? 0);
          setIsOwner(false); // owner role is handled via /auth/me elsewhere; treat as customer here
        })
        .catch(() => setBalance(null));
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Refetch the cost estimate whenever the inputs change
  useEffect(() => {
    if (!feature) return;
    const params = new URLSearchParams({ feature, quality });
    if (duration) params.set("duration", String(duration));
    api.get(`/credits/estimate?${params.toString()}`)
      .then(({ data }) => setCost(Number(data?.credits ?? 0)))
      .catch(() => setCost(null));
  }, [feature, quality, duration]);

  // Also peek at /auth/me for owner detection (don't gate UI, just suppress balance check)
  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setIsOwner((data?.role || data?.user?.role) === "owner"))
      .catch(() => setIsOwner(false));
  }, []);

  const insufficient = !isOwner && balance !== null && cost !== null && balance < cost;

  return (
    <div
      data-testid="credit-cost-strip"
      className={`mb-4 px-4 py-3 border flex flex-col sm:flex-row sm:items-center gap-3 ${
        insufficient
          ? "border-red-500/50 bg-red-500/10"
          : "border-[#D4AF37]/30 bg-[#D4AF37]/5"
      }`}
    >
      {/* Cost */}
      <div className="flex items-center gap-2 min-w-[180px]">
        <Coins className={`w-4 h-4 ${insufficient ? "text-red-300" : "text-[#D4AF37]"}`} />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Estimated cost</p>
          <p className="font-bold text-sm text-[#F4F4F0]" data-testid="credit-cost-value">
            {cost === null ? "…" : <>{cost} <span className="font-normal text-[#A0A0A0]">credits</span></>}
            {label && <span className="ml-2 text-[10px] text-[#5A5A5A] uppercase tracking-wider">· {label}</span>}
          </p>
        </div>
      </div>

      {/* Balance */}
      <div className="flex items-center gap-2 sm:ml-auto">
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Your balance</p>
          <p className={`font-bold text-sm ${insufficient ? "text-red-300" : "text-[#F4F4F0]"}`} data-testid="credit-balance-strip">
            {isOwner ? "Owner · exempt" : (balance === null ? "…" : `${balance.toLocaleString()} credits`)}
          </p>
        </div>
        <Link
          to="/credits"
          data-testid="credit-strip-topup"
          className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 border transition-colors ${
            insufficient
              ? "border-red-500/60 text-red-300 hover:border-red-500 hover:text-red-200"
              : "border-[#D4AF37]/50 text-[#D4AF37] hover:border-[#D4AF37]"
          }`}
        >
          <Plus className="w-3 h-3" /> Top up
        </Link>
      </div>

      {insufficient && (
        <div
          data-testid="credit-strip-insufficient"
          className="w-full sm:w-auto sm:ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-red-300"
        >
          <AlertTriangle className="w-3 h-3" /> Not enough credits — top up to generate
        </div>
      )}
    </div>
  );
}
